import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import {
  Checkbox,
  FormControlLabel,
  styled,
  CheckboxProps,
} from '@mui/material';
import { useId, ReactNode } from 'react';
import { theme } from '~/shared/styles/theme';

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: ReactNode;
}

const InvitationConsentCheckbox = <T extends FieldValues>({
  name,
  control,
  label,
}: Props<T>) => {
  const id = useId();
  const labelId = `${name}-${id}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <StyledFormControlLabel
          control={
            <StyledCheckbox
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              data-testid={`checkbox-${name}`}
              slotProps={{
                input: {
                  'aria-labelledby': labelId,
                },
              }}
            />
          }
          label={<LabelContent id={labelId}>{label}</LabelContent>}
        />
      )}
    />
  );
};

export default InvitationConsentCheckbox;

const StyledFormControlLabel = styled(FormControlLabel)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  margin: 0,
  marginBottom: '28px',
  padding: theme.spacing(4, 10),
  backgroundColor: theme.palette.custom.cream,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.custom.lightGrayBrown}`,
});

const StyledCheckbox = styled(Checkbox)<CheckboxProps>({
  color: theme.palette.custom.green,
  marginRight: theme.spacing(2),
  padding: 0,
  '&.Mui-checked': {
    color: theme.palette.custom.green,
  },
  '& .MuiSvgIcon-root': {
    width: '31px',
    height: '31px',
  },
});

const LabelContent = styled('span')({
  color: theme.palette.custom.mediumBrown,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.4,
  '& a': {
    color: theme.palette.custom.darkBrown,
    fontWeight: 800,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});
